import React, { FunctionComponent } from "react"
import Button from "@mui/material/Button"
import DeterministicNodeDetails from "@features/graph/details/components/DeterministicNodeDetails"
import NodeGroupDetails from "@features/graph/details/components/NodeGroupDetails"
import { Grid } from "@mui/material"
import { ContinuousNodeDetails } from "@features/graph/details/components/ContinuousNodeDetails"
import { DiscreteNodeDetails } from "@features/graph/details/components/DiscreteNodeDetails"
import CategoricalNodeDetails from "@features/graph/details/components/CategoricalNodeDetails"
import ResizableDrawer from "@components/ResizableDrawer/ResizableDrawer"
import { TableNodeDetails } from "@features/graph/details/components/TableNodeDetails"
import { GraphNodeType } from "@custom-types/graphs"
import { LagNodeDetails } from "@features/graph/details/components/LagNodeDetails"
import { ScenarioNodeDetails } from "@features/graph/details/components/ScenarioNodeDetails"
import { ForecastNodeDetails } from "@features/graph/details/components/ForecastNodeDetails"
import { Node } from "reactflow"
import { GraphEditMode, GraphNodeSelection } from "@features/graph/graphViewModel"
import { AddToSelectionButton } from "@features/graph/details/components/AddToSelectionButton"

interface Props {
  editMode: GraphEditMode
  graphKey?: string
  selectedNodes?: GraphNodeSelection
  isOpen: boolean
  closeCallback: () => void
  container?: Element | null
  isMultiselectVisible?: boolean
  onShowNodeResultsClick?: (node?: Node) => void
  localStoragePrefix: string
  isScenarioDesignPage?: boolean
}

export const GraphDetailsContainer: FunctionComponent<Props> = ({
  editMode,
  graphKey,
  selectedNodes,
  isOpen,
  closeCallback,
  container,
  isMultiselectVisible,
  onShowNodeResultsClick,
  localStoragePrefix,
  isScenarioDesignPage = false,
}) => {
  if (!selectedNodes) {
    return <></>
  }
  const selectedNode = selectedNodes.nodes[0]

  if (!selectedNode) {
    closeCallback()
    return <></>
  }

  let component = <></>
  if (selectedNode.type === GraphNodeType.LAG) {
    component = (
      <LagNodeDetails
        editMode={editMode}
        nodeId={selectedNode.id}
        graphKey={graphKey}
        isScenarioDesignPage={isScenarioDesignPage}
      />
    )
  } else if (selectedNode.type === GraphNodeType.DETERMINISTIC) {
    component = (
      <DeterministicNodeDetails editMode={editMode} nodeId={selectedNode.id} graphKey={graphKey} />
    )
  } else if (selectedNode.type === GraphNodeType.SCENARIO) {
    component = (
      <ScenarioNodeDetails editMode={editMode} nodeId={selectedNode.id} graphKey={graphKey} />
    )
  } else if (selectedNode.type === GraphNodeType.FORECAST) {
    component = (
      <ForecastNodeDetails editMode={editMode} nodeId={selectedNode.id} graphKey={graphKey} />
    )
  } else if (selectedNode.type === GraphNodeType.DISCRETE) {
    component = (
      <DiscreteNodeDetails editMode={editMode} nodeId={selectedNode.id} graphKey={graphKey} />
    )
  } else if (selectedNode.type === GraphNodeType.TABLE) {
    component = (
      <TableNodeDetails editMode={editMode} nodeId={selectedNode.id} graphKey={graphKey} />
    )
  } else if (
    selectedNode.type === GraphNodeType.CONTINOUS ||
    selectedNode.type === GraphNodeType.MIXTURE
  ) {
    component = (
      <ContinuousNodeDetails
        editMode={editMode}
        nodeId={selectedNode.id}
        graphKey={graphKey}
        nodeType={selectedNode.type}
      />
    )
  } else if (selectedNode.type === GraphNodeType.GROUP) {
    component = <NodeGroupDetails node={selectedNode} />
  } else if (selectedNode.type === GraphNodeType.CATEGORICAL) {
    component = (
      <CategoricalNodeDetails editMode={editMode} nodeId={selectedNode.id} graphKey={graphKey} />
    )
  }

  return (
    <ResizableDrawer
      SlideProps={{
        appear: false,
      }}
      open={isOpen}
      anchor="right"
      hideBackdrop
      disableEnforceFocus={true}
      transitionDuration={0}
      container={container}
      localStoragePrefix={localStoragePrefix}
      sx={{
        inset: "0 0 -72px 99% !important",
        "&>.MuiPaper-root": {
          position: "absolute",
          borderRadius: "5px !important",
          border: "1px solid #ccc !important",
        },
      }}
      onClose={closeCallback}
    >
      <Grid container direction="column" sx={{ height: "100%" }}>
        <Grid item xs sx={{ overflow: "scroll", width: "100%", pl: 1 }}>
          {component}
        </Grid>
        <Grid item xs="auto" sx={{ display: "flex", ml: 1, mb: 1 }}>
          {isMultiselectVisible &&
            selectedNode?.type !== GraphNodeType.TABLE &&
            selectedNode?.type !== GraphNodeType.GROUP && (
              <>
                <Button
                  onClick={() => {
                    if (typeof onShowNodeResultsClick === "function") {
                      onShowNodeResultsClick(selectedNode)
                    }
                  }}
                  sx={{
                    m: "4px",
                    width: "30%",
                    backgroundColor: "rgba(214,0,42,0.9)",
                    borderRadius: "3px !important",
                  }}
                  variant="contained"
                >
                  Show Node Results
                </Button>
                <AddToSelectionButton selectedNode={selectedNode} />
              </>
            )}
        </Grid>
      </Grid>
    </ResizableDrawer>
  )
}
